/**
 * User List Page
 *
 * Displays all users with filtering, searching, and management capabilities.
 * Admin-only access for user management.
 *
 * @module pages/users/UserList
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Dropdown,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import { getUsers, deleteUser, resetUserPassword } from '../../services/userService';
import ResetPasswordModal from './ResetPasswordModal';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './UserList.css';

/**
 * Format user role for display
 * @param {string} role - User role
 * @returns {Object} Role info with label and variant
 */
const formatUserRole = (role) => {
  const roles = {
    admin: { label: 'Administrador', variant: 'danger', icon: 'bi-shield-fill-check' },
    supervisor: { label: 'Supervisor', variant: 'warning', icon: 'bi-person-check-fill' },
    technician: { label: 'Técnico / Líder', variant: 'primary', icon: 'bi-person-badge-fill' },
    client: { label: 'Cliente', variant: 'success', icon: 'bi-person-fill' },
  };
  return roles[role] || { label: role, variant: 'secondary', icon: 'bi-question-circle' };
};

const UserList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, user: currentUser } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const [filters, setFilters] = useState({
    role: '',
    search: '',
    page: 0,
    pageSize: 20,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [resetting, setResetting] = useState(false);

  /**
   * Restablecer contraseña de otro usuario (cuentas sin correo real).
   * Devuelve `{ error }` al modal para que lo muestre ahí mismo.
   */
  const handleResetPassword = async (password) => {
    if (!userToReset) return { error: new Error('Falta el usuario') };
    setResetting(true);
    const { error: resetError } = await resetUserPassword(userToReset.id, password);
    setResetting(false);
    if (resetError) return { error: resetError };
    toast.success(`Contraseña restablecida para ${userToReset.full_name}`);
    setUserToReset(null);
    return { error: null };
  };

  // Redirect non-admin users
  React.useEffect(() => {
    if (!isAdmin) {
      toast.error('No tienes permisos para acceder a la gestión de usuarios');
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Fetch users with React Query
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => getUsers(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAdmin, // Only fetch if admin
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      toast.success('Usuario eliminado exitosamente');
      queryClient.invalidateQueries(['users']);
      setShowDeleteModal(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      console.error('Delete user error:', error);
      toast.error('Error al eliminar usuario: ' + error.message);
    },
  });

  /**
   * Handle filter changes
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field !== 'page' ? { page: 0 } : {}), // Reset to first page only for non-page filters
    }));
  };

  /**
   * Handle search input
   */
  const handleSearch = (e) => {
    handleFilterChange('search', e.target.value);
  };

  /**
   * Handle role filter
   */
  const handleRoleFilter = (e) => {
    handleFilterChange('role', e.target.value);
  };

  /**
   * Handle row click
   */
  const handleRowClick = (userId) => {
    navigate(`/usuarios/${userId}/editar`);
  };

  /**
   * Handle create new user
   */
  const handleCreateUser = () => {
    navigate('/usuarios/nuevo');
  };

  /**
   * Handle delete user - show confirmation modal
   */
  const handleDeleteClick = (e, user) => {
    e.stopPropagation(); // Prevent row click
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete user
   */
  const confirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  /**
   * Handle pagination
   */
  const handlePreviousPage = () => {
    if (filters.page > 0) {
      handleFilterChange('page', filters.page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((data?.count || 0) / filters.pageSize);
    if (filters.page < totalPages - 1) {
      handleFilterChange('page', filters.page + 1);
    }
  };

  const userColumns = useMemo(() => [
    {
      id: 'name',
      header: 'Nombre',
      accessor: (u) => u.full_name || '',
      minWidth: 180,
      cell: (user) => <strong>{user.full_name || '-'}</strong>,
    },
    {
      id: 'role',
      header: 'Rol',
      accessor: 'role',
      minWidth: 140,
      cell: (user) => {
        const roleInfo = formatUserRole(user.role);
        return (
          <Badge bg={roleInfo.variant}>
            <i className={`bi ${roleInfo.icon} me-1`}></i>
            {roleInfo.label}
          </Badge>
        );
      },
    },
    {
      id: 'company',
      header: 'Compañía',
      accessor: (u) => u.company_name || '',
      minWidth: 160,
      cell: (u) => u.company_name || '-',
    },
    {
      id: 'phone',
      header: 'Teléfono',
      accessor: (u) => u.phone || '',
      minWidth: 130,
      cell: (u) => u.phone || '-',
    },
    {
      id: 'actions',
      header: '',
      sortable: false,
      resizable: false,
      align: 'center',
      noSelect: true,
      minWidth: 50,
      maxWidth: 60,
      cell: (user) => (
        <div data-no-select onClick={(e) => e.stopPropagation()}>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="link"
              className="p-0 text-muted"
              style={{ boxShadow: 'none' }}
              aria-label={`Acciones para ${user.full_name}`}
            >
              <i className="bi bi-three-dots-vertical"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleRowClick(user.id)} data-testid={`edit-user-${user.id}`}>
                <i className="bi bi-pencil me-2"></i>Editar
              </Dropdown.Item>
              {user.id !== currentUser?.id && user.role !== 'admin' && (
                <Dropdown.Item onClick={() => setUserToReset(user)} data-testid={`reset-password-user-${user.id}`}>
                  <i className="bi bi-key me-2"></i>Restablecer contraseña
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => handleDeleteClick({ stopPropagation: () => {} }, user)}
                data-testid={`delete-user-${user.id}`}
              >
                <i className="bi bi-trash me-2"></i>Eliminar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, currentUser?.id]);

  // Loading state
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando usuarios...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar usuarios</Alert.Heading>
          <p>{error.message}</p>
          <Button variant="outline-danger" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  const users = data?.data || [];
  const totalCount = users.length;
  const totalPages = Math.ceil(totalCount / filters.pageSize);
  const currentPage = filters.page + 1;

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Gestión de Usuarios</h2>
                <p className="text-muted">{totalCount} usuarios totales</p>
              </div>
              <Button variant="primary" onClick={handleCreateUser}>
                <i className="bi bi-plus-circle me-2"></i>
                Nuevo Usuario
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-4 align-items-center">
          <Col md={7}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre o correo..."
                value={filters.search}
                onChange={handleSearch}
              />
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Select
              value={filters.role}
              onChange={handleRoleFilter}
              aria-label="Filtrar por rol"
            >
              <option value="">Todos los roles</option>
              <option value="admin">Administrador</option>
              <option value="supervisor">Supervisor</option>
              <option value="technician">Técnico / Líder</option>
              <option value="client">Cliente</option>
            </Form.Select>
          </Col>
          <Col md={2} className="d-flex justify-content-end mt-2 mt-md-0">
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <Card.Body className="p-0">
                {users.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-people" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                    <h5 className="mt-3 text-muted">No se encontraron usuarios</h5>
                    <p className="text-muted">
                      {filters.search || filters.role
                        ? 'Intenta cambiar los filtros'
                        : 'Crea tu primer usuario'}
                    </p>
                  </div>
                ) : (
                  <>
                    {isTableView ? (
                      <DataTable
                        tableId="users"
                        data={users}
                        rowKey={(u) => u.id}
                        onRowClick={(u) => handleRowClick(u.id)}
                        columns={userColumns}
                      />
                    ) : (
                    <Row className="g-3 p-3">
                      {users.map((user, index) => {
                        const roleInfo = formatUserRole(user.role);
                        return (
                          <Col xs={12} sm={6} lg={4} key={user.id}>
                          <Card
                            className="user-mobile-card border"
                            onClick={() => handleRowClick(user.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1 me-2">
                                  <h6 className="mb-1 fw-semibold">{user.full_name || '-'}</h6>
                                </div>
                                <Badge bg={roleInfo.variant} className="flex-shrink-0">
                                  <i className={`bi ${roleInfo.icon} me-1`}></i>
                                  {roleInfo.label}
                                </Badge>
                              </div>

                              <div className="user-card-details mb-3">
                                {user.company_name && (
                                  <p className="mb-1 small text-muted">
                                    <i className="bi bi-building me-1"></i>
                                    {user.company_name}
                                  </p>
                                )}
                                {user.phone && (
                                  <p className="mb-0 small text-muted">
                                    <i className="bi bi-telephone me-1"></i>
                                    {user.phone}
                                  </p>
                                )}
                                {!user.company_name && !user.phone && (
                                  <p className="mb-0 small text-muted fst-italic">
                                    Sin informacion adicional
                                  </p>
                                )}
                              </div>

                              <div className="d-flex justify-content-end pt-2 border-top" onClick={(e) => e.stopPropagation()}>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    variant="link"
                                    className="p-0 text-muted"
                                    style={{ boxShadow: 'none' }}
                                    aria-label={`Acciones para ${user.full_name}`}
                                  >
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() => handleRowClick(user.id)}
                                      data-testid={`mobile-edit-user-${user.id}`}
                                    >
                                      <i className="bi bi-pencil me-2"></i>
                                      Editar
                                    </Dropdown.Item>
                                    {user.id !== currentUser?.id && user.role !== 'admin' && (
                                      <Dropdown.Item
                                        onClick={() => setUserToReset(user)}
                                        data-testid={`mobile-reset-password-user-${user.id}`}
                                      >
                                        <i className="bi bi-key me-2"></i>
                                        Restablecer contraseña
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => handleDeleteClick({ stopPropagation: () => {} }, user)}
                                      data-testid={`mobile-delete-user-${user.id}`}
                                    >
                                      <i className="bi bi-trash me-2"></i>
                                      Eliminar
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </Card.Body>
                          </Card>
                          </Col>
                        );
                      })}
                    </Row>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-3 border-top">
                        <div className="text-muted small">
                          Página {currentPage} de {totalPages}
                        </div>
                        <div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handlePreviousPage}
                            disabled={filters.page === 0}
                            className="me-2"
                          >
                            <i className="bi bi-chevron-left"></i> Anterior
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={filters.page >= totalPages - 1}
                          >
                            Siguiente <i className="bi bi-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ResetPasswordModal
        show={!!userToReset}
        user={userToReset}
        submitting={resetting}
        onHide={() => setUserToReset(null)}
        onSubmit={handleResetPassword}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            ¿Estás seguro que deseas eliminar el usuario <strong>{userToDelete?.full_name || userToDelete?.email}</strong>?
          </p>
          <Alert variant="warning" className="mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Esta acción no se puede deshacer. El usuario y todos sus datos asociados serán eliminados permanentemente.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Eliminar Usuario
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default UserList;
